import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiUsers, FiTrendingUp, FiCheckCircle, FiArrowRight, FiTarget, FiBarChart3, FiSave, FiUserPlus, FiLogIn } = FiIcons;

const Home = () => {
  const benefits = [
    { icon: FiTrendingUp, title: 'Improved Performance', description: 'Teams with high psychological safety are 67% more likely to avoid failures and 47% more likely to exceed performance goals.' },
    { icon: FiUsers, title: 'Better Collaboration', description: 'Open communication and trust lead to more effective teamwork and knowledge sharing.' },
    { icon: FiTarget, title: 'Increased Innovation', description: 'When team members feel safe to share ideas, creativity and innovation flourish.' },
    { icon: FiCheckCircle, title: 'Higher Retention', description: 'Psychologically safe environments reduce turnover and increase employee satisfaction.' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <div className="flex justify-center mb-6">
          <SafeIcon icon={FiShield} className="h-20 w-20 text-blue-600" />
        </div>
        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
          Build Psychologically <span className="text-blue-600"> Safe Teams</span>
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Based on Google's Project Aristotle research, assess and improve your team's psychological safety to unlock peak performance and innovation in your IT consulting projects.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/assessment"
            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
          >
            <span>Start Assessment</span>
            <SafeIcon icon={FiArrowRight} className="h-5 w-5" />
          </Link>
          <Link
            to="/about"
            className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Learn More
          </Link>
        </div>
      </div>

      {/* Features Dashboard Section */}
      <div className="bg-white rounded-xl shadow-lg p-8 sm:p-12 mb-16 overflow-hidden">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Administer and Track Your Team's Performance</h2>
            <p className="text-gray-600 mb-6">
              Gain valuable insights into your team's psychological safety with our intuitive dashboard. Save assessment results, track progress over time, and identify areas for improvement to build a high-performing, resilient team.
            </p>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <SafeIcon icon={FiBarChart3} className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Visualize Data</h4>
                  <p className="text-gray-600">See your team's scores at a glance with clear, concise charts and stats.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <SafeIcon icon={FiTrendingUp} className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Track Progress</h4>
                  <p className="text-gray-600">Monitor improvements over time by saving multiple assessments for each team.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <SafeIcon icon={FiSave} className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Save & Compare</h4>
                  <p className="text-gray-600">Persist your results and compare them with past performance to measure growth.</p>
                </div>
              </li>
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiUserPlus} className="h-5 w-5" />
                <span>Register to Get Started</span>
              </Link>
              <Link
                to="/login"
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiLogIn} className="h-5 w-5" />
                <span>Login to Your Account</span>
              </Link>
            </div>
          </div>
          <div className="mt-8 md:mt-0">
            <img 
              src="https://digitalatips.se/wp-content/uploads/2025/10/dashboard.png" 
              alt="Team Dashboard showing performance metrics" 
              className="rounded-lg shadow-2xl ring-1 ring-gray-900/10"
            />
          </div>
        </div>
      </div>

      {/* Project Aristotle Summary */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Google's Project Aristotle</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <p className="text-gray-600 mb-4">
              Google's extensive research identified psychological safety as the #1 factor that sets successful teams apart. Teams with high psychological safety:
            </p>
            <ul className="space-y-3">
              {[
                'Feel safe to show vulnerability and ask questions',
                'Can discuss problems and tough issues openly',
                "Don't feel rejected for being different",
                'Can take risks without feeling insecure',
              ].map((item, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">The 5 Key Dynamics</h3>
            <ol className="space-y-2">
              {[
                'Psychological Safety',
                'Dependability',
                'Structure & Clarity',
                'Meaning',
                'Impact',
              ].map((dynamic, index) => (
                <li key={index} className="flex items-center space-x-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold">
                    {index + 1}
                  </span>
                  <span className="text-gray-700">{dynamic}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Why Psychological Safety Matters for IT Teams
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
              <div className="flex justify-center mb-4">
                <SafeIcon icon={benefit.icon} className="h-12 w-12 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
              <p className="text-gray-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-8 text-center text-white">
        <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Teams?</h2>
        <p className="text-xl mb-8 text-blue-100">
          Take our comprehensive assessment and get actionable insights to build stronger, more effective teams.
        </p>
        <Link
          to="/assessment"
          className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center space-x-2"
        >
          <span>Start Your Team Assessment</span>
          <SafeIcon icon={FiArrowRight} className="h-5 w-5" />
        </Link>
      </div>
    </div>
  );
};

export default Home;